import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { getSettings } from "../lib/api";
import { Footer } from "../components/Footer";

const LOGO = "/logo-white.png";
const HEADING_RE = /^\d+\.\s+(.+)$/;

// Trasforma il testo libero (con paragrafi separati da righe vuote) in
// paragrafi introduttivi + sezioni numerate ("1. TITOLO\n testo...").
function parse(text) {
  const blocks = (text || "").split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const intro = [];
  const sections = [];
  let current = null;
  for (const block of blocks) {
    const lines = block.split("\n");
    const m = lines[0].match(HEADING_RE);
    if (m) {
      if (current) sections.push(current);
      const rest = lines.slice(1).join("\n").trim();
      current = { h: m[1], p: rest ? [rest] : [] };
    } else if (current) {
      current.p.push(block);
    } else {
      intro.push(block);
    }
  }
  if (current) sections.push(current);
  return { intro, sections };
}

export default function LegalDoc({ field, overline, title, related }) {
  const [text, setText] = useState("");
  const [piva, setPiva] = useState("");

  useEffect(() => {
    getSettings()
      .then((s) => {
        setText(s[field] || "");
        setPiva(s.p_iva || "");
      })
      .catch(() => {});
    window.scrollTo(0, 0);
  }, [field]);

  const { intro, sections } = parse(text);

  return (
    <div className="min-h-screen bg-[#0D110D] text-[#E8E2D2]" data-testid={`legal-doc-${field}`}>
      <header className="border-b border-white/10">
        <div className="max-w-[1000px] mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link">
            <img src={LOGO} alt="Vecchio Cinghiale" className="h-10 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#E8E2D2]/60 hover:text-[#D47738] transition-colors"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-[860px] mx-auto px-5 md:px-8 py-20 md:py-28" data-testid="legal-content">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#D47738] mb-6">
            {overline}
          </p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight mb-6">
            {title}
          </h1>
          <p className="font-mono-accent text-[11px] uppercase tracking-widest text-[#E8E2D2]/40 mb-16">
            P.IVA {piva}
          </p>

          {intro.length > 0 && (
            <div className="space-y-3 text-[#E8E2D2]/65 leading-relaxed text-base mb-12" data-testid="legal-intro">
              {intro.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}

          <div className="space-y-12">
            {sections.map((s, i) => (
              <section key={i}>
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-mono-accent text-sm text-[#D47738]/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-[#E8E2D2]">
                    {s.h}
                  </h2>
                </div>
                <div className="pl-0 md:pl-10 space-y-3 text-[#E8E2D2]/65 leading-relaxed text-base">
                  {s.p.map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {related && (
            <div className="mt-20 pt-10 border-t border-white/10 flex flex-wrap gap-4">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="border border-white/20 px-6 py-3 font-mono-accent text-[11px] uppercase tracking-[0.2em] text-[#E8E2D2] hover:border-[#D47738] hover:text-[#D47738] transition-colors"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
